import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Button, Card, CardContent, CardHeader, CardTitle, Input, Label, Textarea, Checkbox,
} from '@werkstatt/ui';
import { Loader2, CalendarX, CheckCircle2 } from 'lucide-react';
import publicApi from '../../lib/publicApi';
import { readApiError } from '../../lib/apiError';
import type {
  AvailabilityResponse, PublicBookingResponse, PublicConfigResponse,
} from '../../types/public';

function formatPrice(cents: number): string {
  return `${(cents / 100).toFixed(2).replace('.', ',')} €`;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2)
    .map((part) => part[0]).join('').toUpperCase() || 'RH';
}

export default function RentalRequestPage() {
  const { slug = '' } = useParams<{ slug: string }>();

  const [startDate, setStartDate] = useState(todayIso());
  const [endDate, setEndDate] = useState(todayIso());
  const [category, setCategory] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [consent, setConsent] = useState(false);
  const [website, setWebsite] = useState(''); // Honeypot
  const [formError, setFormError] = useState<string | null>(null);

  const configQuery = useQuery({
    queryKey: ['public-rental-config', slug],
    queryFn: async () => {
      const { data } = await publicApi.get<PublicConfigResponse>(`/${slug}/config`);
      return data;
    },
    retry: false,
  });

  const days = useMemo(() => {
    const diff = (new Date(endDate).getTime() - new Date(startDate).getTime())
      / (1000 * 60 * 60 * 24);
    return Math.max(0, Math.round(diff)) + 1;
  }, [startDate, endDate]);

  const rangeValid = startDate && endDate && endDate >= startDate;

  const availabilityQuery = useQuery({
    queryKey: ['public-rental-availability', slug, startDate, endDate],
    queryFn: async () => {
      const { data } = await publicApi.get<AvailabilityResponse>(`/${slug}/availability`, {
        params: { from: startDate, to: endDate },
      });
      return data;
    },
    enabled: Boolean(slug) && Boolean(rangeValid),
  });

  const bookingMutation = useMutation({
    mutationFn: async () => {
      const { data } = await publicApi.post<PublicBookingResponse>(`/${slug}/bookings`, {
        category,
        start_date: startDate,
        end_date: endDate,
        customer_name: name,
        customer_email: email,
        customer_phone: phone || undefined,
        notes: notes || undefined,
        consent,
        website,
      });
      return data;
    },
    onError: (error) => {
      setFormError(readApiError(error, 'Die Anfrage konnte nicht gesendet werden.'));
    },
  });

  if (configQuery.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (configQuery.isError || !configQuery.data) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <CalendarX className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <h1 className="text-xl font-bold">Diese Buchungsseite ist nicht verfügbar</h1>
          <p className="text-muted-foreground mt-2">
            Bitte prüfen Sie den Link oder kontaktieren Sie uns direkt.
          </p>
        </div>
      </div>
    );
  }

  const config = configQuery.data;
  const { tenant } = config;
  const address = [tenant.street, [tenant.postal_code, tenant.city].filter(Boolean).join(' ')]
    .filter(Boolean).join(' · ');

  if (bookingMutation.isSuccess) {
    const result = bookingMutation.data;
    return (
      <div className="min-h-screen bg-slate-50">
        <Header tenant={tenant} address={address} />
        <main className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
          <Card>
            <CardContent className="pt-6 text-center space-y-3">
              <CheckCircle2 className="h-12 w-12 mx-auto text-green-600" />
              <h1 className="text-xl font-bold">Anfrage gesendet</h1>
              <p className="text-muted-foreground">{result.confirmation_text}</p>
              <div className="text-sm bg-muted rounded-md p-4 text-left space-y-1 mt-4">
                <p><span className="text-muted-foreground">Referenz:</span> {result.reference}</p>
                <p><span className="text-muted-foreground">Status:</span> {result.status_label}</p>
                <p><span className="text-muted-foreground">Zeitraum:</span> {result.start_date}
                  {' '}bis {result.end_date}</p>
                {result.total_price_cents > 0 && (
                  <p><span className="text-muted-foreground">Voraussichtlicher Preis:</span>
                    {' '}{formatPrice(result.total_price_cents)}</p>
                )}
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  const availableCategories = availabilityQuery.data?.categories;

  function categoryIsAvailable(cat: string): boolean {
    if (!availableCategories) return true;
    return availableCategories.find((c) => c.category === cat)?.available ?? false;
  }

  function estimatedTotal(cat: string): number | null {
    return availableCategories?.find((c) => c.category === cat)?.total_price_cents ?? null;
  }

  function submit() {
    setFormError(null);
    if (!category) {
      setFormError('Bitte wählen Sie eine Fahrzeugkategorie.');
      return;
    }
    if (!name.trim()) {
      setFormError('Bitte geben Sie Ihren Namen an.');
      return;
    }
    if (!email.trim()) {
      setFormError('Bitte geben Sie Ihre E-Mail-Adresse an.');
      return;
    }
    if (!consent) {
      setFormError('Bitte bestätigen Sie den Datenschutzhinweis.');
      return;
    }
    bookingMutation.mutate();
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header tenant={tenant} address={address} />

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Mietzeitraum</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="start_date">Abholung</Label>
              <Input
                id="start_date" type="date" value={startDate} min={todayIso()}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  if (e.target.value > endDate) setEndDate(e.target.value);
                }}
              />
            </div>
            <div>
              <Label htmlFor="end_date">Rückgabe</Label>
              <Input
                id="end_date" type="date" value={endDate} min={startDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            {rangeValid && (
              <p className="col-span-2 text-sm text-muted-foreground">
                {days} {days === 1 ? 'Tag' : 'Tage'}
                {days < config.min_rental_days
                  && ` — Mindestmietdauer ${config.min_rental_days} Tage`}
                {days > config.max_rental_days
                  && ` — maximale Mietdauer ${config.max_rental_days} Tage`}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Fahrzeugkategorie</CardTitle>
          </CardHeader>
          <CardContent className="grid sm:grid-cols-2 gap-3">
            {config.categories.map((cat) => {
              const available = categoryIsAvailable(cat.category);
              const selected = category === cat.category;
              return (
                <button
                  key={cat.category}
                  type="button"
                  disabled={!available}
                  onClick={() => setCategory(cat.category)}
                  className={`text-left rounded-lg border p-4 transition-colors ${
                    selected ? 'border-primary ring-2 ring-primary/30' : 'border-border'
                  } ${available ? 'hover:border-primary/60' : 'opacity-50 cursor-not-allowed'}`}
                >
                  <p className="font-medium">{cat.label}</p>
                  <p className="text-sm font-semibold mt-1">
                    {formatPrice(cat.price_per_day_cents)} / Tag
                  </p>
                  {cat.price_per_week_cents != null && (
                    <p className="text-xs text-muted-foreground">
                      oder {formatPrice(cat.price_per_week_cents)} / Woche
                    </p>
                  )}
                  {rangeValid && available && estimatedTotal(cat.category) != null && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Gesamt für {days} {days === 1 ? 'Tag' : 'Tage'}:{' '}
                      <span className="font-medium text-foreground">
                        {formatPrice(estimatedTotal(cat.category) as number)}
                      </span>
                    </p>
                  )}
                  {!available && rangeValid && (
                    <p className="text-xs text-destructive mt-1">
                      Im gewählten Zeitraum nicht verfügbar
                    </p>
                  )}
                </button>
              );
            })}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Ihre Kontaktdaten</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="customer_name">Name *</Label>
              <Input id="customer_name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="customer_email">E-Mail *</Label>
                <Input
                  id="customer_email" type="email" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="customer_phone">Telefon</Label>
                <Input
                  id="customer_phone" type="tel" value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="notes">Anmerkungen</Label>
              <Textarea
                id="notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
              />
            </div>

            {/* Honigtopf — für Menschen unsichtbar, Bots füllen es meist aus */}
            <div className="hidden" aria-hidden="true">
              <Label htmlFor="website">Website</Label>
              <Input
                id="website" name="website" tabIndex={-1} autoComplete="off"
                value={website} onChange={(e) => setWebsite(e.target.value)}
              />
            </div>

            <div className="flex items-start gap-2">
              <Checkbox
                id="consent" checked={consent}
                onCheckedChange={(value) => setConsent(value === true)}
              />
              <Label htmlFor="consent" className="font-normal leading-snug">
                Ich bin mit der Verarbeitung meiner Daten zur Bearbeitung dieser
                Mietanfrage einverstanden.
              </Label>
            </div>

            {formError && <p className="text-sm text-destructive">{formError}</p>}

            <Button
              onClick={submit} disabled={bookingMutation.isPending} className="w-full"
            >
              {bookingMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Unverbindlich anfragen
            </Button>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

function Header({ tenant, address }: {
  tenant: PublicConfigResponse['tenant']; address: string;
}) {
  return (
    <header style={{ backgroundColor: tenant.primary_color || '#000000', color: '#fff' }}>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-5 flex items-center gap-4">
        {tenant.logo_url ? (
          <img
            src={tenant.logo_url} alt="" className="h-12 w-12 rounded-lg object-contain
              bg-white/15 p-1"
          />
        ) : (
          <div
            aria-hidden="true"
            className="h-12 w-12 rounded-lg bg-white/20 flex items-center justify-center font-bold"
          >
            {initials(tenant.name)}
          </div>
        )}
        <div className="min-w-0">
          <p className="text-lg sm:text-xl font-bold truncate">{tenant.name}</p>
          {address && (
            <p className="text-xs sm:text-sm opacity-90 truncate">
              {address}{tenant.phone ? ` · ${tenant.phone}` : ''}
            </p>
          )}
        </div>
        <div className="ml-auto text-right hidden sm:block">
          <p className="text-lg font-bold">Mietanfrage</p>
        </div>
      </div>
    </header>
  );
}
